import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import CustomReactSelect from '@components/CustomReactSelect';
import ImportedTag from '@components/ImportedTag';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import useClickOutside from '@hooks/useClickOutside';

import AddedColors from '@features/materials/components/AddedColors';
import MaterialColorAddV2 from '@features/materials/components/MaterialColorAddV2';
import MaterialsLeftPanel from '@features/design/components/MaterialsLeftPanel';
import ShrinkageAndGSM from '@features/design/components/ShrinkageAndGSM';
import HistoryModal from '@features/design/components/HistoryModal';
import ViewBaseColor from '@features/design/components/ViewBaseColor';

import { fetchProductInfo } from '@store/actions/DesignToolAction';
import { fetchEnumFilter, fetchProductMaterials } from '@store/design/action';
import { fetchPinnedMaterials } from '@store/material/action';
import { getCommonFilters, getProductMatrials } from '@store/reducers';
import { fetchAllSuppliersMaterials } from '@store/supplier/action';

import { fetchMaterialUnitType } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import {
	categoryTypeSustainabilityForTrimsAndBranding,
	extraSorting,
	fetchApplicationGarmentsTypeList,
	fetchSupplierByMaterialType,
	getConsumptionUnit,
	getConsumptionUnitForTrimsAccessories,
	getSorting,
	getTabName,
	isFabric,
	isValidFormForBinding,
	MATERIAL_TAG_TYPE,
	MATERIAL_TITLE_NAME,
	someDetailsForTrimsAndBrandingRender,
	STATUS,
	whichUserMaterialBookedCheck
} from '@utils/MaterialsUtil';
import {
	addImageSuffix,
	capitalizeFirstLetter,
	checkAnyUserType,
	isAdmin,
	printApiError,
	validateFloatNumber
} from '@utils/Util';

import { BASE_URL, MATERIALS_TYPE } from '@/constants/constant';

import { useGetMachingCountQuery } from '../api/designDetailsApi';

const ROLES_FOR_DELETE = ['ADMIN', 'MATERIAL_MANAGEMENT', 'INVENTORY_MANAGEMENT'];

const MaterialsV2 = ({ productId, isLoading, setIsLoading }) => {
	const [selectedType, setSelectedType] = useState(MATERIALS_TYPE.FABRIC);
	const [isInventory, setIsInventory] = useState(true);

	const [isShowMaterial, setIsShowMaterial] = useState(false);
	const [productMaterials, setProductMaterials] = useState([]);
	const [productMaterialsTypeObj, setProductMaterialsTypeObj] = useState({});
	const [cellId, setCellId] = useState('');
	const [updatedDataIndex, setUpdatedDataIndex] = useState('');
	const productMaterialsList = useSelector(getProductMatrials);
	const [cloneProductMaterialsList, setCloneProductMaterialsList] = useState('');
	const [allUnitTypes, setAllUnitTypes] = useState([]);
	const [appGarments, setAppGarments] = useState([]);
	const [referenceNumber, setReferenceNumber] = useState('');
	const [selectedSupplier, setSelectedSupplier] = useState(null);
	const [suppliersForFabric, setSuppliersForFabric] = useState([]);
	const [suppliersForTrims, setSuppliersForTrims] = useState([]);
	const [suppliersForBranding, setSuppliersForBranding] = useState([]);
	const [errors, setErrors] = useState({});
	const [shrinkageColumn, setShrinkageColumn] = useState(null);
	const [colorId, setColorId] = useState(null);
	const [colorType, setColorType] = useState('SOLID');
	const [colorDTO, setColorDTO] = useState({});
	const [colorGroupIdList, setColorGroupIdList] = useState([]);
	const [selectedGroupColorList, setSelectedGroupColorList] = useState([]);
	const [selectedPantoneColorList, setSelectedPantoneColorList] = useState([]);
	const [selectedMaterialId, setSelectedMaterialId] = useState(null);
	const [colorSaveType, setColorSaveType] = useState('ADD');
	const [showHistoryModal, setShowHistoryModal] = useState(false);
	const designToolStore = useSelector((store) => store.designToolStore);
	const [selectedPlacement, setSelectedPlacement] = useState([]);
	const placementRef = useRef(null);
	const [selectedMaterial, setSelectedMaterial] = useState(null);
	const consumptionRef = useRef();
	const { refetch } = useGetMachingCountQuery(`?productId=${productId}`);

	const getSelectedPlacementIds = () => {
		return selectedPlacement.filter((item) => !item?.__isNew__)?.map((item) => item.id);
	};

	const isAllowedToRemovePlacement = (placementType) => {
		if (placementType.toLowerCase().trim() === 'body fabric') {
			const bodyFabricCount = productMaterialsTypeObj?.fabric?.reduce((count, item) => {
				return (
					count +
					(item.placementResponseList?.filter(
						(placement) => placement.name.toLowerCase().trim() === 'body fabric'
					).length || 0)
				);
			}, 0);

			return bodyFabricCount > 1;
		}
		return true;
	};

	const createAppGarmentsType = async (newCreatedPlacement, materialType) => {
		try {
			const { data } = await ApiClient.POST(
				`${BASE_URL}/material/app-garments-types`,
				{
					materialType,
					name: newCreatedPlacement
				},
				'',
				true
			);

			const newOption = {
				...data,
				id: data.id,
				label: data.name,
				value: data.name
			};

			setSelectedPlacement((prev) => [...prev, newOption]);

			fetchApplicationGarmentsTypeList(setAppGarments).finally();
		} catch (error) {
			printApiError(error);
		}
	};

	const onRemovePlacement = (placement, material) => {
		if (isAllowedToRemovePlacement(placement.name)) {
			const existingPlacementIds = material?.placementResponseList?.map((item) => item.id);
			const requestBody = {
				placementIdList: existingPlacementIds?.filter(
					(item) => parseInt(item) !== parseInt(placement.id)
				),
				id: material?.id
			};

			handleSubmitUpdatePlacement(requestBody);
		} else {
			toastError('First select the new body fabric then try to delete');
		}
	};

	const handleSubmitUpdatePlacement = async (requestBody) => {
		try {
			setIsLoading(true);
			const { data } = await ApiClient.PUT(
				`${BASE_URL}/product/${productId}/update-material`,
				requestBody,
				'',
				true
			);
			if (data?.success) {
				dispatch(fetchProductMaterials(productId));
				setSelectedPlacement([]);
				setSelectedMaterial(null);
				setCellId('');
			}
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleOutsideClick = () => {
		const requestBody = {
			placementIdList: [...new Set(getSelectedPlacementIds())],
			id: selectedMaterial?.id
		};
		if (requestBody.placementIdList.length > 0) {
			handleSubmitUpdatePlacement(requestBody);
		} else {
			setSelectedMaterial(null);
			setCellId('');
		}
	};

	useClickOutside(placementRef, handleOutsideClick);

	const { id } = useParams();

	const dispatch = useDispatch();
	const clickRef = useRef();

	const commonFilters = useSelector(getCommonFilters);

	useEffect(() => {
		fetchSupplierByMaterialType(
			undefined,
			MATERIALS_TYPE.FABRIC,
			setIsLoading,
			setSuppliersForFabric
		).finally();
		fetchSupplierByMaterialType(
			undefined,
			MATERIALS_TYPE.TRIMS,
			setIsLoading,
			setSuppliersForTrims
		).finally();
		fetchSupplierByMaterialType(
			undefined,
			MATERIALS_TYPE.BRANDING,
			setIsLoading,
			setSuppliersForBranding
		).finally();
		fetchApplicationGarmentsTypeList(setAppGarments).finally();
	}, []);

	useEffect(() => {
		fetchMaterialUnitType().then((res) => {
			setAllUnitTypes(res);
		});
		fetchMaterialListByProductId();
		dispatch(fetchEnumFilter('info.nitex.app.materials.UnitType')).finally();
	}, [dispatch, productId]);

	const fetchMaterialListByProductId = () => {
		setIsLoading(true);
		dispatch(fetchProductMaterials(productId, setIsShowMaterial)).finally(() => {
			setIsLoading(false);
		});
	};
	useEffect(() => {
		let tmpMaterialList = productMaterialsList;
		if (productMaterialsList !== undefined) {
			const cloneMaterialListString = JSON.stringify(productMaterialsList);
			setCloneProductMaterialsList(cloneMaterialListString);
			let tempFabricList = [];
			let tempTrimsList = [];
			let tempBrandingList = [];
			tmpMaterialList = productMaterialsList.map((item, index) => {
				let obj = JSON.parse(JSON.stringify(item));
				obj.productMaterialIndexNumber = index;
				if ([MATERIALS_TYPE.MAIN_FABRIC, MATERIALS_TYPE.EXTRA_FABRIC].includes(obj.materialType)) {
					tempFabricList.push(obj);
				} else if ([MATERIALS_TYPE.TRIMS].includes(obj.materialType)) {
					tempTrimsList.push(obj);
				} else if ([MATERIALS_TYPE.BRANDING].includes(obj.materialType)) {
					tempBrandingList.push(obj);
				}
				return obj;
			});
			let tempObj = {
				fabric: tempFabricList,
				trims: tempTrimsList,
				branding: tempBrandingList
			};
			setProductMaterialsTypeObj(tempObj);
		}
		setProductMaterials(tmpMaterialList);
	}, [productMaterialsList]);

	const getSupplierByMaterialType = (materialType) => {
		if ([MATERIALS_TYPE.MAIN_FABRIC, MATERIALS_TYPE.EXTRA_FABRIC].includes(materialType))
			return suppliersForFabric;
		if ([MATERIALS_TYPE.TRIMS].includes(materialType)) return suppliersForTrims;
		if ([MATERIALS_TYPE.BRANDING].includes(materialType)) return suppliersForBranding;
	};

	const getMaterialDataListForProduct = () => {
		setIsLoading(true);
		if (selectedType !== 'PINNED') {
			let tabName = getTabName(selectedType);
			dispatch(
				fetchAllSuppliersMaterials(
					0,
					20,
					tabName,
					getSorting(selectedType, isInventory),
					extraSorting(commonFilters, isInventory, productId)
				)
			).finally(() => {
				setIsLoading(false);
			});
		} else {
			dispatch(fetchPinnedMaterials({ productId: productId })).finally(() => {
				setIsLoading(false);
			});
		}
	};

	const onUpdateMaterial = async () => {
		setCellId('');
		if (updatedDataIndex !== '') {
			materialUpdateApiCalling(productMaterials[updatedDataIndex]).finally();
		}
	};
	const materialUpdateApiCalling = async (productMaterial) => {
		const requestBody = {
			...productMaterial,
			colorType: null
		};
		setIsLoading(true);
		await ApiClient.PUT(`${BASE_URL}/product/${productId}/update-material`, requestBody, '', true)
			.then(({ data }) => {
				setIsLoading(false);
				setUpdatedDataIndex('');
				if (data) {
					dispatch(fetchProductMaterials(productId)).finally(() => {
						getMaterialDataListForProduct();
					});
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};
	const onUpdateBookedQuantity = async () => {
		setCellId('');
		if (updatedDataIndex !== '') {
			let item = productMaterials[updatedDataIndex];
			let cloneItemList = JSON.parse(cloneProductMaterialsList);
			let cloneItem = cloneItemList.find((cloneItem) => cloneItem.id === item.id);
			let cloneBookedQuantity = cloneItem ? cloneItem.bookedQuantity : 0;
			if (item.bookedQuantity !== undefined) {
				if (item?.issueInventoryStatus !== STATUS.BOOKED) {
					return;
				}
				if (item.stockQuantity + cloneBookedQuantity < item.bookedQuantity) {
					toastError('Your booking qty has crossed the inventory balance');
					return;
				}

				let params = `${id}/${item.id}?materialType=${item.materialType}&bookedQuantity=${item.bookedQuantity}`;
				setIsLoading(true);
				await ApiClient.POST('materialBookedToProduct', '', params)
					.then(() => {
						setIsLoading(false);
						setUpdatedDataIndex('');
					})
					.catch(({ response }) => {
						setIsLoading(false);
						if (response && response.data && response.data.message) {
							toastError(response.data.message);
						} else {
							toastError('Request was unsuccessful.');
						}
					});
			}
		}
	};

	const onOutSideClick = () => {
		setIsLoading(true);
		onUpdateBookedQuantity().finally(() => {
			onUpdateMaterial()
				.then(() => {})
				.finally(() => setIsLoading(false));
		});
	};
	useClickOutside(clickRef, onOutSideClick);

	const onUpdateInfo = (value, index, name) => {
		let newMaterials = [...productMaterials];
		if (name === 'quantity') {
			newMaterials[index][name] = value || null;
		} else {
			newMaterials[index][name] = value;
		}
		setProductMaterials(newMaterials);
		setUpdatedDataIndex(index);
	};

	const onKeyPressUpdate = (event) => {
		if (event.key === 'Enter' || event.key === 'Escape') {
			onOutSideClick();
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const onSetShowMaterial = (set) => setIsShowMaterial(set);

	const onRemoveMaterialFromProduct = async (material) => {
		setIsLoading(true);
		await ApiClient.POST('removeMaterialFromProductList', {}, `${productId}/${material.id}`)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					/** updated for basic info */
					dispatch(fetchProductInfo(productId));
					getMaterialDataListForProduct();
					dispatch(fetchProductMaterials(productId)).finally();
				}
				refetch();
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onAddNewColors = () => {
		dispatch(fetchProductMaterials(productId)).finally(() => {
			setIsLoading(false);
		});
	};

	const onAddColor = (materialId) => {
		const itemDetails = productMaterials.find((item) => item?.id === materialId);
		const hasColors =
			itemDetails?.materialColorResponse?.colorResponse ||
			itemDetails?.materialColorResponse?.colorGroupResponseList?.length > 0;

		setColorSaveType(hasColors ? 'EDIT' : 'ADD');
		setColorId(itemDetails?.materialColorResponse?.colorResponse?.id || null);
		if (hasColors) setSelectedMaterialId(materialId);
	};

	const getMaterialColors = () => {
		const itemDetails = productMaterials.find((item) => item.id === selectedMaterialId);
		if (!itemDetails) return;

		if (colorSaveType === 'EDIT') {
			const firstColor =
				Object.keys(itemDetails?.materialColorResponse.colorResponse)?.length > 0
					? itemDetails?.materialColorResponse?.colorResponse
					: itemDetails?.baseMaterialColorResponse?.colorResponse;

			setColorId(itemDetails?.materialColorResponse?.colorResponse?.id || null);

			if (firstColor?.compositeColorList !== undefined) {
				setSelectedPantoneColorList(firstColor?.compositeColorList);
			} else {
				if (Object.keys(firstColor).length !== 0) {
					setSelectedPantoneColorList([firstColor]);
				} else {
					setSelectedPantoneColorList([]);
				}
			}
			const groupColors =
				itemDetails?.materialColorResponse?.colorGroupResponseList?.length > 0
					? itemDetails?.materialColorResponse?.colorGroupResponseList
					: itemDetails?.baseMaterialColorResponse?.colorGroupResponseList;
			setSelectedGroupColorList(groupColors);
			const colorType = itemDetails?.materialColorResponse?.colorType?.constant
				? itemDetails?.materialColorResponse?.colorType?.constant
				: itemDetails?.baseMaterialColorResponse?.colorType?.constant;
			setColorType(colorType);
		} else {
			setColorId(null);
			setSelectedPantoneColorList([]);
			setSelectedGroupColorList([]);
			setColorType('SOLID');
		}
	};

	useEffect(() => {
		if (selectedMaterialId) {
			getMaterialColors();
		}
	}, [colorSaveType, selectedMaterialId]);

	const renderAddColor = (materialId) => {
		return (
			<MaterialColorAddV2
				colorId={colorId}
				colorDTO={colorDTO}
				setColorDTO={setColorDTO}
				colorType={colorType === undefined ? 'SOLID' : colorType}
				setColorType={setColorType}
				colorGroupIdList={colorGroupIdList}
				setColorGroupIdList={setColorGroupIdList}
				selectedGroupColorList={selectedGroupColorList}
				setSelectedGroupColorList={setSelectedGroupColorList}
				selectedPantoneColorList={selectedPantoneColorList}
				setSelectedPantoneColorList={setSelectedPantoneColorList}
				isShowSubmit={true}
				materialId={materialId}
				actionSuccess={() => onAddNewColors()}
				renderFrom="DESIGN"
			/>
		);
	};

	const onAddPin = async (materialId) => {
		setIsLoading(true);
		await ApiClient.POST('materialPin', {}, `${id}/material/${materialId}/pin`)
			.then(({ data }) => {
				getMaterialDataListForProduct();
				toastSuccess(data.message);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response.data?.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const getUnitTypes = (unit) => {
		return allUnitTypes?.map((item) => {
			if (unit === item.value) return item.label;
		});
	};

	const conditionWiseBookedColumnAdd = (item, idx) => {
		if (item?.bookedQuantity !== undefined && item?.issueInventoryStatus === STATUS.BOOKED) {
			return (
				<td
					className="cursor-pointer"
					onClick={() => {
						setTimeout(() => {
							setCellId(`${idx}-BOOKED_QTY`);
						}, 0);
					}}
				>
					{cellId === `${idx}-BOOKED_QTY` ? (
						<div className="d-flex f-consumption booked-qty" ref={consumptionRef}>
							<input
								autoFocus
								type="number"
								value={item?.bookedQuantity}
								min={1}
								onKeyPress={validateFloatNumber}
								onChange={(e) => onUpdateInfo(e.target.value, idx, 'bookedQuantity')}
								onKeyDown={(event) => {
									if (event.key === 'Enter' || event.key === 'Escape') {
										onUpdateBookedQuantity();
										event.preventDefault();
										event.stopPropagation();
									}
								}}
							/>
							<span className="regular-14 gray_dark_01">
								{isFabric(item?.materialType)
									? getConsumptionUnit(item)
									: getConsumptionUnitForTrimsAccessories(item)}
							</span>
						</div>
					) : (
						<p>
							{item?.bookedQuantity}&nbsp;
							{isFabric(item?.materialType)
								? getConsumptionUnit(item)
								: getConsumptionUnitForTrimsAccessories(item)}
						</p>
					)}
				</td>
			);
		} else if (item?.bookedQuantity !== undefined && item?.issueInventoryStatus !== STATUS.BOOKED) {
			return (
				<td>
					<>
						{item?.issueInventoryStatus === STATUS.CANCELLED && (
							<p className="regular-14 mb-2 red"> Booking cancel</p>
						)}
						{item?.issueInventoryStatus === STATUS.ACTIVE && (
							<p className="regular-14 mb-2 green">
								Issued: {item?.issuedQuantity} &nbsp;
								{isFabric(item?.materialType)
									? getConsumptionUnit(item)
									: getConsumptionUnitForTrimsAccessories(item)}
							</p>
						)}
						<p
							className={
								'regular-14 gray_dark_01 ' +
								(item?.issueInventoryStatus === STATUS.CANCELLED ? 'red' : '')
							}
						>
							{item?.bookedQuantity}&nbsp;
							{isFabric(item?.materialType)
								? getConsumptionUnit(item)
								: getConsumptionUnitForTrimsAccessories(item)}
						</p>
					</>
				</td>
			);
		} else {
			return <td></td>;
		}
	};

	const materialDetailsRender = (title, objectInfo) => {
		if (title === MATERIAL_TITLE_NAME.FABRIC) {
			return (
				<>
					<p className="regular-12 gray_dark_03">{objectInfo?.fabricType}</p>
					<p className="regular-14 gray_dark_01 mb-2">{objectInfo?.construction}</p>
					{objectInfo?.compositionPartList?.map((compositionPart, index) => {
						return (
							<p
								key={`composition_part_${index}`}
								className="regular-14 gray_dark_01 text-nowrap"
							>
								{compositionPart?.percentage} {'% '}
								{compositionPart?.fiberName?.toProperCase()}
							</p>
						);
					})}
					<p className="regular-14 gray_dark_01 mt-2">
						{objectInfo?.gsm} {objectInfo?.weightUnit}{' '}
						{objectInfo?.weightType && `(${objectInfo?.weightType})`}
					</p>
				</>
			);
		} else if ([MATERIAL_TITLE_NAME.TRIMS, MATERIAL_TITLE_NAME.BRANDING].includes(title)) {
			return (
				<>
					<p className="weight-500 mb-2 gray_dark_01">
						{objectInfo?.subCategoryType ? objectInfo?.subCategoryType?.name : ''}
					</p>
					<p className="regular-14 gray_dark_01 mt-2">
						{categoryTypeSustainabilityForTrimsAndBranding(objectInfo)}
					</p>
					{someDetailsForTrimsAndBrandingRender(objectInfo)}
				</>
			);
		}
	};

	const supplierTag = (item) => {
		if (selectedSupplier?.id === undefined) {
			setErrors((prevState) => ({ ...prevState, supplierError: 'Supplier is required' }));
			return;
		} else {
			setErrors((prevState) => ({ ...prevState, supplierError: undefined }));
		}
		setIsLoading(true);
		ApiClient.POST('addSupplierCopyMaterialTag', {
			materialId: item?.id,
			oldSupplierId: item?.supplierId,
			supplierId: selectedSupplier?.id,
			referenceNumber: referenceNumber,
			requestedPage: 'MATERIAL'
		})
			.then(() => {
				fetchMaterialListByProductId();
				setCellId('');
			})
			.catch(() => {
				setIsLoading(false);
			});
	};
	const onRemoveSupplier = (e, item) => {
		e.stopPropagation();
		setIsLoading(true);
		ApiClient.DELETE('deleteSupplierCopyMaterialTag', '', item?.id + '/' + item?.supplierId, false)
			.then(() => fetchMaterialListByProductId())
			.catch(() => {
				setIsLoading(false);
			});
	};
	const onSelectForCustomDropDown = (obj, key) => {
		setSelectedSupplier(obj);
		isValidFormForBinding(obj?.value, 'supplierError', key.toProperCase() + ' is required', setErrors);
	};

	// const getAppGarments = () => {
	// 	return appGarments?.filter((garmentsType) =>
	// 		['MAIN_FABRIC', 'EXTRA_FABRIC'].includes(garmentsType.materialType)
	// 	);
	// };

	const handleEditPlacement = (item) => {
		const placementList = item?.placementResponseList?.map((placement) => ({
			...placement,
			label: placement.name,
			value: placement.name
		}));
		setSelectedMaterial(item);
		setSelectedPlacement(placementList);
	};

	const productMaterialItemRender = (item, titleName) => {
		let objectInfo = null;
		if (MATERIAL_TITLE_NAME.FABRIC === titleName) objectInfo = item.fabricDetailsTableResponse;
		else if ([MATERIAL_TITLE_NAME.TRIMS, MATERIAL_TITLE_NAME.BRANDING].includes(titleName))
			objectInfo = item.trimsAndBrandingTableResponse;

		return (
			<tr key={item.id}>
				<td className={!item.documentPath ? 'no-hover' : ''}>
					<CommonImage
						src={addImageSuffix(item.documentPath) || '/images/fabric-default.png'}
						alt=""
						className="cell-img pr-2"
						isStatic={item.documentPath ? false : true}
					/>
				</td>
				<td
					className="cursor-pointer placement-column"
					onClick={() => {
						setTimeout(() => {
							setCellId(`${item.productMaterialIndexNumber}-TYPE`);
							handleEditPlacement(item);
						}, 0);
					}}
				>
					{[MATERIAL_TITLE_NAME.TRIMS, MATERIAL_TITLE_NAME.BRANDING].includes(titleName) && (
						<p className="regular-14 gray_dark_01 mb-2">
							{objectInfo?.subCategoryType?.category}
						</p>
					)}
					{cellId === `${item.productMaterialIndexNumber}-TYPE` ? (
						<div ref={placementRef} onClick={(e) => e.stopPropagation()}>
							<CustomReactSelect
								type={isAdmin() ? 'CREATABLE' : ''}
								title={'Select'}
								onSetSelectedItem={(selectItem) => {
									setSelectedPlacement(selectItem);
								}}
								toolTipLength={35}
								allItems={appGarments}
								selectedValue={selectedPlacement}
								isMulti={true}
								onCreateOption={(newPlacement) => {
									createAppGarmentsType(newPlacement, item?.materialType);
								}}
								defaultMenuIsOpen={true}
								autoFocus={true}
								onRemoveValue={(selected) => {
									onRemovePlacement(selected, item);
								}}
								isClearable={false}
								randerCreateLabelJSX={(input) => (
									<div>
										<span className="semibold-14 gray_dark_01">Create:</span>{' '}
										<span className="regular-14 gray_dark_01">{input}</span>
									</div>
								)}
							/>
						</div>
					) : (
						<div className="d-flex flex-wrap gap-2">
							{item?.placementResponseList?.length > 0 ? (
								item?.placementResponseList?.map((placement) => (
									<span key={placement?.id} className="chip gray-chip">
										{placement?.name}
									</span>
								))
							) : (
								<span className="regular-14 gray_dark_01">
									Add Placement{' '}
									<CommonImage
										src={`/images/chevron_down.svg`}
										alt="chevron_down"
										className="ml-2"
									/>
								</span>
							)}
						</div>
					)}
				</td>
				<td
					onClick={() => {
						setTimeout(() => {
							setCellId(`${item.productMaterialIndexNumber}-REFERENCE_NUMBER`);
						}, 0);
					}}
				>
					{cellId === `${item.productMaterialIndexNumber}-REFERENCE_NUMBER` &&
					!whichUserMaterialBookedCheck() ? (
						<input
							onBlur={() => onUpdateMaterial()}
							autoFocus
							style={{ display: 'block' }}
							type="text"
							autoComplete="off"
							value={item.referenceNumber}
							onChange={(e) =>
								onUpdateInfo(
									e.target.value,
									item.productMaterialIndexNumber,
									'referenceNumber'
								)
							}
							onKeyDown={(event) => {
								if (event.key === 'Enter' || event.key === 'Escape') {
									onUpdateMaterial();
									event.preventDefault();
									event.stopPropagation();
								}
							}}
						/>
					) : (
						<div>
							<p className="regular-14 gray_dark_01">{item.referenceNumber}</p>
							{item?.fabricDetailsTableResponse?.pairType && (
								<p className="mt-4">
									{capitalizeFirstLetter(item?.fabricDetailsTableResponse?.pairType)}
								</p>
							)}

							{item.fabricDetailsTableResponse?.pairMaterialReferenceNumber && (
								<p className="regular-14 gray_dark_01">
									{item.fabricDetailsTableResponse?.pairMaterialReferenceNumber}
								</p>
							)}
						</div>
					)}
				</td>
				<td>{materialDetailsRender(titleName, objectInfo)}</td>
				<td className="position-relative color-column">
					{(item?.baseMaterialColorResponse?.colorGroupResponseList?.length > 0 ||
						(item?.materialColorResponse?.colorResponse?.id &&
							item?.baseMaterialColorResponse?.colorResponse?.id &&
							item?.baseMaterialColorResponse?.colorResponse?.id !==
								item?.materialColorResponse?.colorResponse?.id)) && (
						<ViewBaseColor colorInfo={item?.baseMaterialColorResponse} />
					)}
					<div className="d-flex justify-content-between align-items-center">
						<AddedColors
							colorGroupResponseList={
								item?.materialColorResponse?.colorGroupResponseList?.length > 0
									? item?.materialColorResponse?.colorGroupResponseList
									: item?.baseMaterialColorResponse?.colorGroupResponseList
							}
							colorResponse={
								Object.keys(item.materialColorResponse.colorResponse).length > 0
									? item?.materialColorResponse?.colorResponse
									: item?.baseMaterialColorResponse?.colorResponse
							}
							colorType={
								item?.materialColorResponse?.colorType?.name
									? item?.materialColorResponse?.colorType?.name
									: item?.baseMaterialColorResponse?.colorType?.name
							}
						/>
						<div className="material-colors">
							<div className="actions text-center">
								<div className="colors-row" onClick={() => onAddColor(item?.id)}>
									{renderAddColor(item?.id, item?.colorResponseList)}
								</div>
							</div>
						</div>
					</div>
				</td>
				{MATERIAL_TITLE_NAME.FABRIC === titleName && (
					<td
						onClick={() => {
							setShrinkageColumn(item);
						}}
						className="cursor-pointer"
					>
						{(item.fabricDetailsTableResponse?.shrinkageLength >= 0 ||
							item.fabricDetailsTableResponse?.shrinkageWidth >= 0) && <p>Shrinkage:</p>}
						<p className="mb-1">
							{item?.fabricDetailsTableResponse?.shrinkageLength >= 0 ? (
								<span>L= {item.fabricDetailsTableResponse.shrinkageLength}%, </span>
							) : null}
							{item?.fabricDetailsTableResponse?.shrinkageWidth >= 0 ? (
								<span>W= {item.fabricDetailsTableResponse.shrinkageWidth}%</span>
							) : null}
						</p>
						{item?.fabricDetailsTableResponse?.solidDyedGsm >= 0 ? (
							<p>S/D: {item?.fabricDetailsTableResponse?.solidDyedGsm} GSM </p>
						) : null}
						{item?.fabricDetailsTableResponse?.afterWashGsm >= 0 ? (
							<p className="mt-1">A/W: {item?.fabricDetailsTableResponse?.afterWashGsm} GSM</p>
						) : null}

						{shrinkageColumn?.id === item?.id && (
							<div>
								<ShrinkageAndGSM
									setShrinkageColumn={setShrinkageColumn}
									shrinkageColumn={shrinkageColumn}
									productId={id}
									fetchMaterialListByProductId={fetchMaterialListByProductId}
									item={item?.fabricDetailsTableResponse}
								/>
							</div>
						)}
					</td>
				)}

				{conditionWiseBookedColumnAdd(item, item.productMaterialIndexNumber)}
				<td
					className="cursor-pointer consumption-column"
					onClick={() => {
						setTimeout(() => {
							setCellId(`${item.productMaterialIndexNumber}-QUANTITY`);
						}, 0);
					}}
				>
					{cellId === `${item.productMaterialIndexNumber}-QUANTITY` ? (
						<div className="d-flex f-consumption" ref={clickRef}>
							<input
								autoFocus
								type="number"
								value={item.quantity}
								min={1}
								onKeyPress={validateFloatNumber}
								onChange={(e) =>
									onUpdateInfo(e.target.value, item.productMaterialIndexNumber, 'quantity')
								}
								onKeyDown={(event) => onKeyPressUpdate(event)}
							/>
							<select
								style={{ display: 'block' }}
								value={item.quantityUnit}
								onChange={(e) =>
									onUpdateInfo(
										e.target.value,
										item.productMaterialIndexNumber,
										'quantityUnit'
									)
								}
								onKeyDown={(event) => onKeyPressUpdate(event)}
							>
								<option value="">Unit</option>
								{allUnitTypes.map((unit) => {
									return (
										<option value={unit.value} key={unit.value}>
											{unit.label}
										</option>
									);
								})}
							</select>
						</div>
					) : (
						<span className="regular-14 gray_dark_01">
							{item.quantity}&nbsp;
							{item.quantity && getUnitTypes(item.quantityUnit)}
						</span>
					)}
				</td>
				<td
					className="cursor-pointer supplier-column"
					onClick={(event) => {
						event.stopPropagation();
						setTimeout(() => {
							setSelectedSupplier({ id: item?.supplierId, value: item?.supplierName });
							setReferenceNumber(item?.supplierRefNumber);
							setCellId(`${item?.productMaterialIndexNumber}-SUPPLIER_INFO`);
						}, 0);
					}}
				>
					{cellId === `${item.productMaterialIndexNumber}-SUPPLIER_INFO` &&
					!whichUserMaterialBookedCheck() ? (
						<div className="supplier-popup shadow-24dp" onClick={(e) => e.stopPropagation()}>
							<div>
								<div className="form-group">
									<label htmlFor="supplier">Supplier*</label>
									<CustomDropdown
										type="addItem"
										title={'Supplier'}
										onItemClick={(value) => {
											onSelectForCustomDropDown(value, MATERIAL_TAG_TYPE.SUPPLIER);
										}}
										items={getSupplierByMaterialType(item?.materialType)}
										selectedItem={selectedSupplier?.value}
									/>
									{errors.supplierError && <p className="error">{errors.supplierError}</p>}
								</div>
								<div className="form-group mb-0">
									<label htmlFor="refNumber">Supplier reference code</label>
									<input
										type="text"
										autoComplete="off"
										id="refNumber"
										className="form-control"
										aria-label="Text input with dropdown button"
										placeholder=""
										value={referenceNumber}
										onChange={(e) => setReferenceNumber(e.target.value)}
									/>
								</div>
							</div>
							<div className="footer-buttons text-right mt-2 pt-2">
								<span
									className="regular-14 gray_dark_02 cursor-pointer"
									onClick={() => setCellId('')}
								>
									CANCEL
								</span>
								<span
									className="regular-14 gray_dark_01 ml-3 cursor-pointer"
									onClick={() => supplierTag(item)}
								>
									SUBMIT
								</span>
							</div>
						</div>
					) : (
						<div>
							{item?.isImported && (
								<div className="mb-2">
									<ImportedTag />
								</div>
							)}

							<p className="regular-14 gray_dark_01">{item?.supplierRefNumber}</p>
							<span className="regular-12 gray_dark_02 mt-1">{item?.supplierName}</span>
							{item?.supplierName && !whichUserMaterialBookedCheck() && (
								<img
									className="color-delete column-hover-icon ml-2"
									src={`${process.env.PUBLIC_URL}/icons/delete_red.svg`}
									alt="delete"
									onClick={(e) => onRemoveSupplier(e, item)}
								/>
							)}
						</div>
					)}
				</td>
				<td
					onClick={() => {
						setTimeout(() => {
							setCellId(`${item.productMaterialIndexNumber}-DESCRIPTION`);
						}, 0);
					}}
				>
					{cellId === `${item.productMaterialIndexNumber}-DESCRIPTION` ? (
						<input
							onBlur={() => onUpdateMaterial()}
							autoFocus
							style={{ display: 'block' }}
							type="text"
							autoComplete="off"
							value={item.description}
							onChange={(e) =>
								onUpdateInfo(e.target.value, item.productMaterialIndexNumber, 'description')
							}
							onKeyDown={(event) => {
								if (event.key === 'Enter' || event.key === 'Escape') {
									onUpdateMaterial();
									event.preventDefault();
									event.stopPropagation();
								}
							}}
						/>
					) : (
						<span className="regular-14 gray_dark_01">{item.description}</span>
					)}
				</td>
				<td>
					<div>
						<img
							src={`${process.env.PUBLIC_URL}/icons/more_icon.svg`}
							alt="more"
							className="more"
							data-toggle="dropdown"
							aria-expanded="false"
						/>
						<ul className="dropdown-menu dropdown-menu-right shadow-lg" x-placement="bottom-end">
							{item?.issueInventoryStatus !== STATUS.APPROVED &&
							designToolStore.availabilityStatus === 'PUBLISHED' ? (
								checkAnyUserType(ROLES_FOR_DELETE) && (
									<li onClick={() => onRemoveMaterialFromProduct(item)}>
										<span>Delete</span>
									</li>
								)
							) : (
								<li onClick={() => onRemoveMaterialFromProduct(item)}>
									<span>Delete</span>
								</li>
							)}
							<li onClick={() => onAddPin(item.id)}>
								<span>Add to pin</span>
							</li>
						</ul>
					</div>
				</td>
			</tr>
		);
	};
	const productMaterialsTableRender = (list, titleName) => {
		if (list !== undefined && list.length > 0) {
			return (
				<>
					<p className="semibold-12 gray_dark_02 mt-3 mb-2">{titleName}</p>
					<div className="material-list-table">
						<div className="update-material-list-table table-responsive">
							<table className="table">
								<thead>
									<tr>
										<th>Image</th>
										<th>
											{[
												MATERIAL_TITLE_NAME.TRIMS,
												MATERIAL_TITLE_NAME.BRANDING
											].includes(titleName)
												? 'Material type & '
												: ''}
											Placement
										</th>
										<th>Reference</th>
										<th>Material details</th>
										<th>Color</th>
										{MATERIAL_TITLE_NAME.FABRIC === titleName && <th>Shrinkage & GSM</th>}

										<th>Nitex inventory book Qty</th>
										<th className="consumption">Sample consumption</th>
										<th>Supplier</th>
										<th>Remarks</th>
										<th />
									</tr>
								</thead>
								<tbody>
									{list?.map((item) => productMaterialItemRender(item, titleName))}
								</tbody>
							</table>
						</div>
					</div>
				</>
			);
		} else return '';
	};

	const onCloseHistoryModal = () => {
		setShowHistoryModal(false);
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="dashboard-container">
				<div className="materials-tab-content">
					<MaterialsLeftPanel
						isShowMaterial={isShowMaterial}
						onSetShowMaterial={onSetShowMaterial}
						productId={productId}
						onAddPin={onAddPin}
						isMainFabric={productMaterials?.length === 0}
						isLoading={isLoading}
						setIsLoading={setIsLoading}
						selectedType={selectedType}
						setSelectedType={setSelectedType}
						isInventory={isInventory}
						setIsInventory={setIsInventory}
						availabilityStatus={designToolStore.availabilityStatus}
					/>
					<div className={isShowMaterial ? 'material-collapse-panel' : ''}>
						<div className="d-flex justify-content-between align-items-center">
							<button className="button size32 " onClick={() => onSetShowMaterial(true)}>
								Add material
								<CommonImage src={'/icons/Rightarrow-white.svg'} alt={'right arrow'} />
							</button>
							<button
								className="button text-only size32"
								onClick={() => setShowHistoryModal(true)}
							>
								<span className="text-underline">History</span>
							</button>
						</div>
						{Object.keys(productMaterialsTypeObj).length === 3 &&
							productMaterialsTableRender(
								productMaterialsTypeObj?.fabric,
								MATERIAL_TITLE_NAME.FABRIC
							)}
						{Object.keys(productMaterialsTypeObj).length === 3 &&
							productMaterialsTableRender(
								productMaterialsTypeObj?.trims,
								MATERIAL_TITLE_NAME.TRIMS
							)}
						{Object.keys(productMaterialsTypeObj).length === 3 &&
							productMaterialsTableRender(
								productMaterialsTypeObj?.branding,
								MATERIAL_TITLE_NAME.BRANDING
							)}
					</div>
				</div>
			</div>

			{showHistoryModal && <HistoryModal onCloseModal={onCloseHistoryModal} />}
		</LoaderComponent>
	);
};

export default MaterialsV2;
